import React from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert } from '@mui/material';
import { SentimentScoreLabels, SentimentScoreColors, EventImpactLevelLabels, EventImpactLevelColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

const NewsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const newsSentiments = data.news_sentiments || [];
    const socialSentiments = data.social_sentiments || [];
    const events = data.events || [];

    return (
        <Box>
            {/* News Sentiments */}
            <SectionTitle>News Sentiments</SectionTitle>
            {newsSentiments.length === 0 ? <EmptyState message="No news sentiments" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Headline</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell>Impact</TableCell>
                                <TableCell>Published</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {newsSentiments.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.headline || '-'}
                                    </TableCell>
                                    <TableCell>{row.source_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={SentimentScoreLabels[row.sentiment] || row.sentiment || '-'} size="small" color={SentimentScoreColors[row.sentiment] || 'default'} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={EventImpactLevelLabels[row.impact_level] || row.impact_level || '-'} size="small" color={EventImpactLevelColors[row.impact_level] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.published_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Social Sentiments */}
            <SectionTitle>Social Sentiments</SectionTitle>
            {socialSentiments.length === 0 ? <EmptyState message="No social sentiment data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Platform</TableCell>
                                <TableCell align="right">Mentions</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell align="right">Bullish Ratio</TableCell>
                                <TableCell>Analysis Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {socialSentiments.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.platform || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.mention_count)}</TableCell>
                                    <TableCell>
                                        <Chip label={SentimentScoreLabels[row.sentiment] || row.sentiment || '-'} size="small" color={SentimentScoreColors[row.sentiment] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.bullish_ratio)}</TableCell>
                                    <TableCell>{formatDate(row.analysis_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Events */}
            <SectionTitle>Events</SectionTitle>
            {events.length === 0 ? <EmptyState message="No events" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Impact</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.event_name || '-'}</TableCell>
                                    <TableCell>{row.event_type || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={EventImpactLevelLabels[row.impact_level] || row.impact_level || '-'} size="small" color={EventImpactLevelColors[row.impact_level] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDate(row.event_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default NewsTab;
